import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AgentAuditOperationDetail from '../Components/AgentAuditOperationDetail';
import Breadcrumbs from '../Components/Breadcrumbs';
import PageLoader from '../Components/PageLoader';
import { Tab, Tabs } from '../Components/Tabs';
import TestAgentModal from '../Components/TestAgentModal';

import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { PromptApi } from '../Services/PromptApiProviderTypes';
import { AuditOperation } from '../Services/TaskScheduleApiProviderTypes';
import { AGENT_TAB_LABELS, AGENT_TABS, AgentTab, agentTabPath } from './AgentHelpers';

function AgentAuditOperationContent() {
  const navigate = useNavigate();
  const { organizationId, agentId, operationId } = useParams();
  const [searchParams] = useSearchParams();
  const runKey = operationId || searchParams.get('runKey');

  const { getAgentAuditOperationById, getPrompts, getAgentPrompts } = useAgentApi();

  const getAgentAuditOperationByIdRef = useRef(getAgentAuditOperationById);
  getAgentAuditOperationByIdRef.current = getAgentAuditOperationById;

  const [agentLoading, setAgentLoading] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [promptIds, setPromptIds] = useState<string[]>([]);
  const [prompts, setPrompts] = useState<PromptApi[]>([]);
  const [isPromptRealtime, setIsPromptRealtime] = useState<boolean>(false);

  const [detailAudit, setDetailAudit] = useState<AuditOperation | null>(null);

  // ── Prompt realtime detection ─────────────────────────────────────────
  useEffect(() => {
    if (!promptIds.length || !prompts?.length) {
      setIsPromptRealtime(false);
      return;
    }
    setIsPromptRealtime(
      prompts.some((p) => promptIds.includes(p.id) && p.pattern_type === 'realtime'),
    );
  }, [promptIds, prompts]);

  // ── Fetch the agent's prompts (for the test panel) ────────────────────
  useEffect(() => {
    if (agentId === 'new-agent') return;

    const f = async () => {
      setAgentLoading(true);
      try {
        const [promptsList, agentPrompts] = await Promise.all([
          getPrompts(),
          getAgentPrompts(agentId),
        ]);
        setPromptIds(agentPrompts?.map((p) => p.id) ?? []);
        setPrompts(promptsList);
      } catch (err) {
        console.error('Error fetching agent data:', err);
        setError('Failed to load agent data');
      } finally {
        setAgentLoading(false);
      }
    };

    f();
  }, [agentId]);

  // ── Fetch audit detail by runKey ──────────────────────────────────────
  useEffect(() => {
    if (!runKey || agentId === 'new-agent') return;

    const f = async () => {
      setDetailLoading(true);
      try {
        const detail = await getAgentAuditOperationByIdRef.current(agentId, runKey);
        if (detail) {
          setDetailAudit(detail);
        } else {
          setError('Audit operation not found');
        }
      } catch (err) {
        console.error('Error fetching audit detail:', err);
        setError('Failed to load audit operation detail');
      } finally {
        setDetailLoading(false);
      }
    };

    f();
  }, [agentId, runKey]);

  // Other tabs lead back to the agent page; Audit returns to the list of runs
  const openAgentTab = (tab: string): void => {
    navigate(agentTabPath(organizationId, agentId, tab as AgentTab));
  };

  const handleBack = (): void => openAgentTab(AGENT_TABS.AUDIT);

  const loading = agentLoading || detailLoading;

  const auditRun = (
    <div className="row d-flex gap">
      <div className="radius-yes p-2 bg-yes col-6">
        <div className="gap d-flex flex-row-to-col flex-column">
          <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
            {loading ? (
              <PageLoader />
            ) : error ? (
              <div>Error: {error}</div>
            ) : detailAudit ? (
              <AgentAuditOperationDetail
                agentAudit={detailAudit}
                onClose={handleBack}
                showId={true}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="radius-yes p-2 bg-yes radius col-6">
        <h2 className="h2-title">Test Agent</h2>
        <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column border-yes">
          <TestAgentModal isOpen={true} isPromptRealtime={isPromptRealtime} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="container">
      <Breadcrumbs />
      {/* A run sits under the agent's Audit tab, so the page keeps the agent's tabs */}
      <Tabs activeTab={AGENT_TABS.AUDIT} onTabChange={openAgentTab}>
        {Object.values(AGENT_TABS).map((tab) => (
          <Tab key={tab} label={AGENT_TAB_LABELS[tab]} value={tab} wrapText={false}>
            {tab === AGENT_TABS.AUDIT ? auditRun : null}
          </Tab>
        ))}
      </Tabs>
    </div>
  );
}

function AgentAuditOperationPage() {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <AgentAuditOperationContent />
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentAuditOperationPage;
